/**
 * Wrapper fetch vers le backend paja-agent.
 * Inject le JWT Clerk pour authentification.
 *
 * Si VITE_USE_MOCKS=true, retourne des mocks au lieu d'appeler le backend.
 * Pratique pour développer le frontend sans avoir le backend démarré.
 */
import {
  mockKpis,
  mockApprovals,
  mockScripts,
  mockAgents,
  mockCalendar,
  mockFinance,
  mockSystem,
  mockPrompts,
  mockClientsAdmin,
} from './mock-data';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';
const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === 'true';

async function apiCall<T>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown; getToken: () => Promise<string | null> }
): Promise<T> {
  const token = await options.getToken();
  // exactOptionalPropertyTypes: on construit l'init sans key body si absent,
  // au lieu de passer body: undefined (incompatible avec BodyInit | null).
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  const resp = await fetch(`${API_BASE_URL}${path}`, init);

  if (!resp.ok) {
    const txt = await resp.text();
    throw new Error(`API ${resp.status}: ${txt}`);
  }
  return resp.json() as Promise<T>;
}

// Petite latence simulée pour les mocks
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const api = {
  getKpis: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockKpis;
    }
    return apiCall<typeof mockKpis>(`/api/clients/${slug}/kpis`, { getToken });
  },

  listApprovals: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockApprovals;
    }
    return apiCall<typeof mockApprovals>(`/api/clients/${slug}/approvals`, { getToken });
  },

  approveApproval: async (slug: string, id: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(150);
      return { ok: true } as const;
    }
    return apiCall<{ ok: true }>(`/api/clients/${slug}/approvals/${id}/approve`, { method: 'POST', getToken });
  },

  rejectApproval: async (slug: string, id: string, reason: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(150);
      return { ok: true } as const;
    }
    return apiCall<{ ok: true }>(`/api/clients/${slug}/approvals/${id}/reject`, {
      method: 'POST',
      body: { reason },
      getToken,
    });
  },

  listContentScripts: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockScripts;
    }
    return apiCall<typeof mockScripts>(`/api/clients/${slug}/content/scripts`, { getToken });
  },

  listAgents: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockAgents;
    }
    return apiCall<typeof mockAgents>(`/api/clients/${slug}/agents`, { getToken });
  },

  // Calendar
  listCalendar: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockCalendar;
    }
    return apiCall<typeof mockCalendar>(`/api/clients/${slug}/calendar`, { getToken });
  },

  // Finance
  getFinance: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockFinance;
    }
    return apiCall<typeof mockFinance>(`/api/clients/${slug}/finance`, { getToken });
  },

  // System
  getSystem: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockSystem;
    }
    return apiCall<typeof mockSystem>(`/api/clients/${slug}/system`, { getToken });
  },

  // Prompts (settings)
  listPrompts: async (slug: string, getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockPrompts;
    }
    return apiCall<typeof mockPrompts>(`/api/clients/${slug}/settings/prompts`, { getToken });
  },

  // Admin : liste tous les clients
  adminListClients: async (getToken: () => Promise<string | null>) => {
    if (USE_MOCKS) {
      await sleep(200);
      return mockClientsAdmin;
    }
    return apiCall<typeof mockClientsAdmin>(`/api/admin/clients`, { getToken });
  },
};
